/**
 * Допустимая дата рождения — одна на все три платформы.
 *
 * До этого границы были выдуманы в трёх местах по-разному и все три неверно:
 * web разрешал `1900-01-01…2100-12-31`, то есть дату рождения В БУДУЩЕМ; натив
 * принимал 1900…текущий год, сервер — те же 1900…2100 и заодно 31 февраля.
 * Дата уходит в расчёт натальной карты, и «родился в 2100» — это не строгость
 * ради строгости, а бессмысленный ответ модели.
 *
 * **Нижняя граница — 13 лет, и она не выдумана**: ровно эту аудиторию мы
 * заявили в Google Play (13–15, 16–17, 18+). Верхняя — сто лет: это про
 * правдоподобие, а не про запрет, и она отсекает промашку пальцем вроде «1926».
 *
 * **Границы годовые, а не по дню в день.** Родившийся в декабре предельного
 * года формально младше тринадцати на несколько месяцев. Это осознанно: ворот
 * по возрасту тут нет и проверять его нам нечем — дата нужна астрологии, — а
 * годовая граница объяснима человеку и делает выбор года простым списком.
 */

/** Кого считаем своей аудиторией. Нижняя — из заявленной в Google Play. */
export const MIN_AGE = 13
export const MAX_AGE = 100

/** Годы, между которыми лежит допустимый год рождения (включительно). */
export function birthYears(now: Date = new Date()): { min: number; max: number } {
  const year = now.getUTCFullYear()
  return { min: year - MAX_AGE, max: year - MIN_AGE }
}

/**
 * Сколько дней в месяце. Февраль считаем по-настоящему, с високосным годом:
 * 29 февраля — обычная дата рождения, и терять её нельзя.
 */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

/**
 * Настоящая ли это дата и в допустимых ли она годах.
 *
 * Проверяем и календарь тоже: 31 апреля проходит любую проверку диапазонов,
 * но такого дня не существует.
 */
export function isValidBirth(
  d: { year?: number; month?: number; day?: number } | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!d) return false
  const { year, month, day } = d
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false
  const years = birthYears(now)
  if (year! < years.min || year! > years.max) return false
  if (month! < 1 || month! > 12) return false
  return day! >= 1 && day! <= daysInMonth(year!, month!)
}

/**
 * Границы для `<input type="date">` в вебе — тем же правилом, что и везде.
 * Браузер после этого сам не даст выбрать ни будущее, ни 1900 год.
 */
export function birthIsoRange(now: Date = new Date()): { min: string; max: string } {
  const years = birthYears(now)
  return { min: `${years.min}-01-01`, max: `${years.max}-12-31` }
}
